import {
  List,
  Datagrid,
  TextField,
  BooleanField,
  Resource,
  Filter,
  TextInput,
  SelectInput,
} from "react-admin";

// Filters for the field-definitions audit. displayName is a free-text search; every other
// column gets an exact-match dropdown. fieldKey is intentionally NOT filterable (it is the raw
// internal key — the human-facing displayName search covers lookups). Filtering runs
// client-side (see dataProvider getList for `fieldDefinitionsCoverage`): the coverage endpoint
// returns the full view unfiltered, so filters are applied in the browser.
const YES_NO = [
  { id: "true", name: "Sí" },
  { id: "false", name: "No" },
];
const SECTION_CHOICES = [
  { id: "PRODUCT", name: "Artículo (PRODUCT)" },
  { id: "MATERIALS", name: "Materiales" },
  { id: "SUPPLIERS", name: "Proveedores" },
  { id: "RETAIL PACKAGING", name: "Packaging" },
];
const LEVEL_CHOICES = [
  { id: "L1", name: "L1" },
  { id: "L2", name: "L2" },
  { id: "L3", name: "L3" },
];

const FieldDefinitionsFilters = (props) => (
  <Filter {...props}>
    <TextInput source="displayName" label="Buscar por nombre" alwaysOn resettable />
    <SelectInput source="section" label="Section" choices={SECTION_CHOICES} alwaysOn resettable />
    <SelectInput source="level" label="Level" choices={LEVEL_CHOICES} alwaysOn resettable />
    <SelectInput source="hasAssumption" label="Has assumption" choices={YES_NO} alwaysOn resettable />
    <SelectInput source="isCovered" label="Covered" choices={YES_NO} resettable />
    <SelectInput source="hasMismatch" label="Mismatch" choices={YES_NO} resettable />
  </Filter>
);

// Read-only audit of every field definition and its assumptions. Combines DB tables
// (field_definition + field_assumption) with the code-side EvaluatorMeta, flagging
// drift: `hasMismatch` = admin marked hasAssumption but code/DB don't back it.
// Rows expand inline (rowClick="expand") — no getOne endpoint on the backend.
//
// Custom dense layout instead of RA's SimpleShowLayout (which stacks every field with large
// vertical padding — hence the "muy espaciado" complaint). Facts render as an inline label:value
// grid; assumptions as a compact table.
const YesNo = ({ v }) => (
  <span style={{ color: v ? "#059669" : "#94a3b8", fontWeight: 600 }}>{v ? "Sí" : "No"}</span>
);

const Fact = ({ label, children }) => (
  <div style={{ fontSize: 13 }}>
    <span style={{ color: "#94a3b8", marginRight: 6 }}>{label}:</span>
    {children}
  </div>
);

const th = { textAlign: "left", padding: "4px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "#94a3b8", borderBottom: "1px solid #e2e8f0" };
const td = { padding: "4px 10px", fontSize: 12, borderBottom: "1px solid #f1f5f9", verticalAlign: "top" };

const CoverageDetail = ({ record = {} }) => {
  const assumptions = record.assumptions || [];
  return (
    <div style={{ padding: "10px 12px", background: "#f8fafc", borderRadius: 6 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "6px 20px", marginBottom: assumptions.length ? 12 : 0 }}>
        <Fact label="Entity table">{record.entityTable || "—"}</Fact>
        <Fact label="Filter context">{record.filterContext || "—"}</Fact>
        <Fact label="Static assumption"><YesNo v={record.hasStaticAssumption} /></Fact>
        <Fact label="Dynamic assumption"><YesNo v={record.hasDynamicAssumption} /></Fact>
        <Fact label="Code resolveDefaultBulk"><YesNo v={record.hasResolveDefault} /></Fact>
      </div>

      {assumptions.length > 0 ? (
        <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 6, overflow: "hidden" }}>
          <thead>
            <tr>
              <th style={th}>Label</th>
              <th style={th}>Dynamic</th>
              <th style={th}>Value</th>
              <th style={th}>Confidence</th>
              <th style={th}>Geography</th>
              <th style={th}>Source</th>
            </tr>
          </thead>
          <tbody>
            {assumptions.map((a, i) => (
              <tr key={i}>
                <td style={td}>{a.label || "—"}</td>
                <td style={td}><YesNo v={a.isDynamic} /></td>
                <td style={td}>{a.value ?? "—"}</td>
                <td style={td}>{a.confidence || "—"}</td>
                <td style={td}>{a.geography || "—"}</td>
                <td style={td}>{a.source || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div style={{ fontSize: 12, color: "#94a3b8" }}>Sin assumptions.</div>
      )}
    </div>
  );
};

const FieldDefinitionsCoverageList = (props) => (
  <List {...props} perPage={50} bulkActionButtons={false} filters={<FieldDefinitionsFilters />}>
    <div style={{ marginTop: "15px" }}>
      <Datagrid rowClick="expand" expand={<CoverageDetail />}>
        <TextField source="fieldKey" label="Field key" />
        <TextField source="displayName" label="Display name" />
        <TextField source="section" label="Section" />
        <TextField source="level" label="Level" />
        <BooleanField source="hasAssumption" label="Has assumption (admin)" />
        <BooleanField source="isCovered" label="Covered" />
        <BooleanField source="hasMismatch" label="Mismatch" />
      </Datagrid>
    </div>
  </List>
);

const fieldDefinitionsCoverageResource = () => (
  <Resource
    name="fieldDefinitionsCoverage"
    options={{ label: "Field definitions & assumptions", menuGroup: "Bcome" }}
    list={FieldDefinitionsCoverageList}
  />
);

export default fieldDefinitionsCoverageResource;
